import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import ForumIcon from '@mui/icons-material/Forum';
import PhoneIcon from '@mui/icons-material/Phone';
import { Avatar, Badge, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState, type ReactElement } from 'react';
import { type Gesprekspartner, useContactmomentMutaties } from '../api/contactmomenten.ts';
import type { Contactkanaal, Contactmoment } from '../api/typen.ts';
import { useSessie } from '../auth/SessieContext.tsx';
import { BevestigDialoog } from './BevestigDialoog.tsx';
import { ContactmomentDialoog } from './ContactmomentDialoog.tsx';
import { useMeldingen } from './Meldingen.tsx';

export const kanalen: Record<
  Contactkanaal,
  { icoon: (grootte: number) => ReactElement; kleur: 'primary' | 'secondary' | 'success' | 'warning' }
> = {
  telefoon: { icoon: (grootte) => <PhoneIcon sx={{ fontSize: grootte }} />, kleur: 'primary' },
  email: { icoon: (grootte) => <EmailIcon sx={{ fontSize: grootte }} />, kleur: 'secondary' },
  gesprek: { icoon: (grootte) => <ForumIcon sx={{ fontSize: grootte }} />, kleur: 'success' },
  bericht: { icoon: (grootte) => <ChatIcon sx={{ fontSize: grootte }} />, kleur: 'warning' },
};

/** Met wie of waarover een reeks gesprekken gaat, zoals de chat die opent bij een klik op een gesprek. */
export interface Chatpartner {
  soort: Gesprekspartner;
  id: string;
}

const soorten: readonly Gesprekspartner[] = ['kandidaat', 'contactpersoon', 'bedrijf', 'vacature'];

/**
 * Bepaalt bij welke chat een gesprek hoort: de kandidaat, anders de contactpersoon, anders het bedrijf of de vacature.
 * @param moment Het gesprek.
 * @returns De chatpartner, of `null` als het gesprek nergens aan hangt.
 */
export function chatpartnerVan(moment: Contactmoment): Chatpartner | null {
  if (moment.kandidaatId) {
    return { soort: 'kandidaat', id: moment.kandidaatId };
  }
  if (moment.contactpersoonId) {
    return { soort: 'contactpersoon', id: moment.contactpersoonId };
  }
  if (moment.bedrijfId) {
    return { soort: 'bedrijf', id: moment.bedrijfId };
  }
  return moment.vacatureId ? { soort: 'vacature', id: moment.vacatureId } : null;
}

/**
 * Zet een chatpartner om naar een waarde voor in het adres, zoals `kandidaat.<id>`.
 * @param partner De chatpartner.
 * @returns De waarde.
 */
export function chatparameter(partner: Chatpartner): string {
  return `${partner.soort}.${partner.id}`;
}

/**
 * Leest een chatpartner uit het adres.
 * @param waarde De waarde, zoals `kandidaat.<id>`.
 * @returns De chatpartner, of `null` als de waarde leeg of onbekend is.
 */
export function leesChatparameter(waarde: string): Chatpartner | null {
  const [soort, id] = waarde.split('.');
  return soort && id && (soorten as readonly string[]).includes(soort) ? { soort: soort as Gesprekspartner, id } : null;
}

/** Het kanaal als gekleurd icoon, met een pijltje voor inkomend of uitgaand. */
export function Kanaalicoon({ moment, grootte = 36 }: { moment: Contactmoment; grootte?: number }) {
  const { icoon, kleur } = kanalen[moment.kanaal];
  const Pijl = moment.richting === 'inkomend' ? CallReceivedIcon : CallMadeIcon;
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <Box
          sx={{
            display: 'flex',
            p: '2px',
            borderRadius: '50%',
            bgcolor: 'background.paper',
            color: 'text.secondary',
            boxShadow: 1,
          }}
        >
          <Pijl sx={{ fontSize: 11 }} />
        </Box>
      }
      aria-hidden
    >
      <Avatar
        sx={(thema) => ({
          width: grootte,
          height: grootte,
          color: thema.palette[kleur].main,
          bgcolor: alpha(thema.palette[kleur].main, 0.12),
        })}
      >
        {icoon(grootte / 2)}
      </Avatar>
    </Badge>
  );
}

/**
 * Wijzigen en verwijderen van gesprekken, met de dialogen die daarbij horen. Alleen wie een gesprek vastlegde, of
 * een beheerder, mag dat.
 * @returns Of een gesprek te bewerken is, functies om de dialogen te openen en de dialogen zelf.
 */
export function useGesprekBewerken() {
  const { ik, isBeheerder } = useSessie();
  const { meld, meldFout } = useMeldingen();
  const mutaties = useContactmomentMutaties();
  const [wijzigen, setWijzigen] = useState<Contactmoment | null>(null);
  const [verwijderen, setVerwijderen] = useState<Contactmoment | null>(null);

  const dialogen = (
    <>
      <ContactmomentDialoog
        open={wijzigen !== null}
        onSluit={() => setWijzigen(null)}
        bestaand={wijzigen ?? undefined}
      />
      <BevestigDialoog
        open={verwijderen !== null}
        titel="Gesprek verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(null)}
        onBevestig={() => {
          if (verwijderen) {
            mutaties.verwijder
              .mutateAsync(verwijderen.id)
              .then(() => {
                meld('Gesprek verwijderd');
                setVerwijderen(null);
              })
              .catch(meldFout);
          }
        }}
      >
        Weet je zeker dat je dit gesprek wilt verwijderen?
      </BevestigDialoog>
    </>
  );

  return {
    magBewerken: (moment: Contactmoment) => isBeheerder || moment.gebruikerId === ik?.gebruiker.id,
    isVanMij: (moment: Contactmoment) => moment.gebruikerId === ik?.gebruiker.id,
    wijzig: setWijzigen,
    verwijder: setVerwijderen,
    dialogen,
  };
}
